<script setup lang="ts">
import { onMounted, computed, reactive, toRef } from 'vue';
import { useContentRender, useLocalStorage } from '@zoomcatalog/composable';
import {
  ZButton,
  ZMenu,
  ZMenuItem,
  ZTBreak,
  useMenu,
  useSVG,
} from '@zoomcatalog/core-ui';
import { ZDotIcon, ZMenuIcon } from '@zoomcatalog/icons';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/composable/store-utils/useAuthStore';
import { useProjectStore } from '@/composable/store-utils/useProjectStore';
import { usePaginationInstance } from '@/composable/usePagination';
import { useTrackingHandlers } from '@/composable/useTracking';
import useDownloadService from '@/service/request-handlers/useDownloadService';
import { DEFAULT_PAGE_DIMENSIONS } from '@/service/ts-definitions';
import { settings } from '@/settings/DynamicSettings';
import CompanySwitcher from './CompanySwitcher.vue';
import CreateYourOwn from './CreateYourOwn.vue';

const locale = {
  DOWNLOAD: 'Download PDF',
  SIGN_OUT: 'Sign out',
};

const { QC_SIMPLE_PAGE_URL } = settings;

const route = useRoute();
const pagination = usePaginationInstance();
const contentRenderTasks = useLocalStorage(
  `contentRenderTasks-${route.params.projectId?.toString()}`
);
const trackingHandlers = useTrackingHandlers();
const authStore = useAuthStore();
const projectStore = useProjectStore();
const { createDownload, updateDownload, getPendingDownloads } =
  useDownloadService();
const { SVGProps: MenuIconProps } = useSVG();
const { SVGProps: DotIconProps } = useSVG({
  customClasses: 'menu-option__icon',
  customStyles: {
    width: '12px',
    height: '12px',
    color: '#75C205',
  },
});
const [menuProps, menuHandlers] = useMenu();
const menuStyle = { width: '224px' };

const projectId = computed(() => route.params.projectId?.toString() ?? '');
const isTemplate = computed(() =>
  Boolean(
    projectStore.projectData.value.is_template &&
      !projectStore.projectData.value.parent
  )
);
const [PAGE_WIDTH, PAGE_HEIGHT] = DEFAULT_PAGE_DIMENSIONS;

const contentRender = useContentRender({
  config: reactive({
    width: toRef(
      () =>
        projectStore.projectData.value.settings.designType?.dimensions[0] ??
        PAGE_WIDTH
    ),
    height: toRef(
      () =>
        projectStore.projectData.value.settings.designType?.dimensions[1] ??
        PAGE_HEIGHT
    ),
    pages: toRef(() => pagination.totalPages),
    url: QC_SIMPLE_PAGE_URL(projectId.value),
    ignore_crop: 1,
    optimizer: false,
    format: 'pdf',
  }),
  onCreateDownload: async (format, data) => {
    const response = await createDownload(projectId.value, format, data);
    return response.data;
  },
  onUpdateDownload: async (taskId, status) => {
    const response = await updateDownload(projectId.value, taskId, {
      status,
    });
    return response.data;
  },
  onGetPendingDownloads: async () => {
    const response = await getPendingDownloads(projectId.value);
    return response.data;
  },
  getPersistedPendingDownloads() {
    const storedContentRenderTasks = contentRenderTasks.get();
    if (!storedContentRenderTasks) return [];

    try {
      return JSON.parse(storedContentRenderTasks);
    } catch (error) {
      return [];
    }
  },
  setPersistedPendingDownloads(pendingDownloads) {
    contentRenderTasks.set(JSON.stringify(pendingDownloads));
  },
  cleanPersistedPendingDownloads() {
    contentRenderTasks.remove();
  },
});

function downloadDesign() {
  trackingHandlers.sendDesignDownloadEvent();
  contentRender.download();
  menuHandlers.closeMenu();
  if (!contentRender.hasReadyDownloads.value) return;
}

onMounted(() => {
  contentRender.checkDownloads(true);
});
</script>

<template>
  <div class="primary-action-bar">
    <div class="primary-action-bar__options">
      <ZButton
        size="sm"
        variant="icon"
        :disabled="contentRender.isDownloading.value"
        :pending="contentRender.isDownloading.value"
        @click="menuHandlers.openMenu"
      >
        <ZMenuIcon v-bind="MenuIconProps" />
      </ZButton>

      <ZMenu
        v-bind="menuProps"
        :style="menuStyle"
        @close="menuHandlers.closeMenu"
      >
        <ZMenuItem button @click="downloadDesign">
          <div class="menu-option__caption">
            {{ locale.DOWNLOAD }}
          </div>
          <ZDotIcon
            v-if="contentRender.hasReadyDownloads.value"
            v-bind="DotIconProps"
          />
        </ZMenuItem>
        <ZMenuItem
          v-if="authStore.isAuthenticated.value"
          button
          @click="authStore.removeAuthorization(), menuHandlers.closeMenu()"
        >
          {{ locale.SIGN_OUT }}
        </ZMenuItem>
      </ZMenu>
    </div>

    <template v-if="pagination.screenType === 'desktop' && isTemplate">
      <div class="primary-action-bar__make-your-own">
        <CreateYourOwn />
      </div>
      <template v-if="authStore.isAuthenticated.value">
        <ZTBreak direction="vertical" />
        <div class="primary-action-bar__company-switcher">
          <CompanySwitcher />
        </div>
      </template>
    </template>
  </div>

  <div
    v-if="pagination.screenType === 'mobile' && isTemplate && false"
    class="secondary-action-bar"
  >
    <div class="secondary-action-bar__make-your-own">
      <CreateYourOwn />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.primary-action-bar {
  @apply w-full h-[56px];
  @apply bg-white border-b border-gray-500;
  @apply flex items-center py-3 px-4;

  &__make-your-own {
    @apply ml-auto;
  }
}
.menu-option__icon {
  @apply ml-3;
}
.secondary-action-bar {
  @apply w-full min-h-[56px];
  @apply bg-white border-b border-gray-500;
  @apply flex items-center py-3 px-4;

  &__make-your-own {
    @apply w-full;
  }
}
</style>
